// Phase 18 · Community · Report modal
import React, { useState } from "react";
import { X, Flag } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
    { value: "spam", label: "Spam" },
    { value: "harassment", label: "Harassment" },
    { value: "offensive", label: "Offensive content" },
    { value: "misleading", label: "Misleading / disinformation" },
    { value: "other", label: "Other" },
];

export default function ReportModal({ postId, onClose }) {
    const [reason, setReason] = useState("spam");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/posts/${postId}/report`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ reason, notes }),
            });
            if (!r.ok) {
                toast.error(r.status === 429 ? "Report limit reached" : "Report failed");
                return;
            }
            toast.success("Report submitted — thanks");
            onClose?.();
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="report-modal"
        >
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl w-full max-w-md p-6 relative">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-200"
                    data-testid="report-modal-close"
                >
                    <X size={18} />
                </button>
                <div className="flex items-center gap-2 mb-4">
                    <Flag size={18} className="text-[#FAFAFA]" />
                    <h2 className="text-lg font-semibold text-neutral-100">Report post</h2>
                </div>
                <p className="text-xs text-neutral-500 mb-4">
                    Send this post to the moderation team. Choose the reason that fits best.
                </p>
                <div className="space-y-2 mb-4">
                    {REASONS.map((r) => (
                        <label
                            key={r.value}
                            className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${reason === r.value ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-[#1A1A1A] hover:bg-white/5"}`}
                        >
                            <input
                                type="radio"
                                checked={reason === r.value}
                                onChange={() => setReason(r.value)}
                                className="accent-[#FFFFFF]"
                                data-testid={`report-reason-${r.value}`}
                            />
                            <span className="text-sm text-neutral-200">{r.label}</span>
                        </label>
                    ))}
                </div>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional context (optional)"
                    rows={3}
                    maxLength={500}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                    data-testid="report-notes"
                />
                <div className="flex justify-end gap-2 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-4 py-2 text-sm bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded disabled:opacity-40"
                        data-testid="report-submit"
                    >
                        {busy ? "..." : "Submit report"}
                    </button>
                </div>
            </div>
        </div>
    );
}
